import { DateTime } from "luxon"
import { CalculationType, DataType, dataTypeByFieldType, Insight, LabelFormat, Operator } from "."
import { CaseDetails } from "../caseDetails"
import { Clause } from "../view"
import { UserRole } from "../caseUser"
import { CalculatedResults, CalculationResults } from "./calculatedResults"
import _ from "lodash"
import { CaseUser, Field, CalculationType as CalculatedFieldType } from ".."
import { caseColumns } from "../caseColumns"
import { TimeSpentAsValue } from "../timeSpentAsValue"

interface DateRange {
    start: DateTime,
    end: DateTime
}

export interface GroupedSearchResults {
    labels: { [key: string]: string },
    results: { [key: string]: CaseDetails[] },
    labelFormat: LabelFormat
}

export interface IncludedInsight {
    insight: Insight,
    results: CalculatedResults
}

/// Code kept similar to DataService calculations to make it easier to keep aligned if modified
export class ClientSideCalculations {
	public reRunCalculations (calculatedResults: CalculatedResults, insight: Insight, allFields: Field[]) : CalculatedResults {
		const groupedSearchResults: GroupedSearchResults = {
			labelFormat: calculatedResults.labelFormat,
			labels: calculatedResults.results.toDictionaryMapValue(c => c.value, c => c.label),
			results: calculatedResults.results.toDictionaryMapValue(c => c.value, c => c.results)
		}

		const newCalculations = this.runCalculations(groupedSearchResults, insight, allFields)
		newCalculations.calculations = calculatedResults.calculations

		return newCalculations
	}

	public runCalculations (groupedSearchResults: GroupedSearchResults, insight: Insight, allFields: Field[]) : CalculatedResults {
		const dayCount = this.getDayCount(insight)

		const calculationFunctionsList: any[] = []
		const calculationLabels: { [key: string]: { [key: string]:string } } = {}

		for (let calculation of insight.calculations) {
			let getFieldValue = (caseDetails: CaseDetails) => null as any
			if (calculation.field) {
				if (calculation.field.startsWith("fields.")) {
					const fieldId = calculation.field.substr(7)
					getFieldValue = (caseDetails: CaseDetails) => this.getValueFromField(caseDetails, fieldId)
				} else {
					const fieldName = calculation.field
					getFieldValue = (caseDetails: CaseDetails) => this.getValueFromBuiltInField(caseDetails, fieldName)
				}
			}

			let getGroupByValue: any
			if (calculation.groupBy) {
				if (calculation.groupBy.startsWith("fields.")) {
					const fieldId = calculation.groupBy.substr(7)
					const field = allFields.find(c => c.id === fieldId)

					const nullLabel =
                        field && (field.dataType === "list-multiple" || field.dataType === "map-marker") ? "None" : "Not set"

					getGroupByValue = (caseDetails: CaseDetails) => this.getLabeledValueFromField(caseDetails, fieldId, nullLabel)
				} else {
					getGroupByValue = (caseDetails: CaseDetails) => this.getLabeledValueFromProperty(caseDetails, calculation.groupBy!)
				}
			}

			let calculationAppend = (
				index: string,
				calculations: { [key: string]: any},
				caseDetails: CaseDetails,
				groupBy: string | null,
				insightGroupKey: string | null) : void => {}

			let getNumericValue = (caseDetails: CaseDetails, insightsGroupKey: string | null) => {
				const value = getFieldValue(caseDetails)
				if (this.isUserRole(value)) {
					if (insight.groupByDataType === DataType.userRole) {
						let totalTime: number = this.getUsersTotalTime(value.users, insightsGroupKey)
						if (value.subRoles) {
							totalTime += value.subRoles
								.map(c => this.getUsersTotalTime(c.users, insightsGroupKey))
								.reduce((a, b) => a + b)
						}
						return totalTime
					} else {
						return value.totalTime
					}
				}

				if (this.isTimeSpent(value)) {
					if (!calculation.timeSpentCalculation) {
						throw new Error("No time spent calculation parameters set")
					}

					const timeSpentTotal = value.totals.find(c => c.fieldValue === calculation.timeSpentCalculation!.searchLabel)
					return timeSpentTotal ? timeSpentTotal.hoursSpent : 0
				}

				return typeof value === "number" ? value : null
			}

			switch (calculation.calcType) {
				case CalculationType.Count:
				case CalculationType.CountPerDay:
					calculationAppend = (index, calculations, caseDetails, groupBy, insightGroupKey) => {
						if (groupBy) {
							const calculationValues = calculations[index]
							const existingCalculationValue = calculationValues[groupBy]
							if (existingCalculationValue) {
								calculationValues[groupBy] = existingCalculationValue + 1
							} else {
								calculationValues[groupBy] = 1
							}
						} else {
							const existingCalculationValue = calculations[index]
							if (existingCalculationValue) {
								calculations[index] = existingCalculationValue + 1
							} else {
								calculations[index] = 1
							}
						}
					}
					break
				case CalculationType.Average:
				case CalculationType.Total:
					calculationAppend = (index, calculations, caseDetails, groupBy, insightGroupKey) => {
						var value = getNumericValue(caseDetails, insightGroupKey)
						if (value) {
							if (groupBy) {
								const calculationValues = calculations[index]
								const existingCalculationValue = calculationValues[groupBy]
								if (existingCalculationValue) {
									calculationValues[groupBy] = existingCalculationValue + value
								} else {
									calculationValues[groupBy] = value
								}
							} else {
								const existingCalculationValue = calculations[index]
								if (existingCalculationValue) {
									calculations[index] = existingCalculationValue + value
								} else {
									calculations[index] = value
								}
							}
						}
					}
					break
				case CalculationType.Max:
					calculationAppend = (index, calculations, caseValue, groupBy, insightGroupKey) => {
						var value = getNumericValue(caseValue, insightGroupKey)
						if (value != null) {
							if (groupBy) {
								const calculationValues = calculations[index]
								const existingCalculationValue = calculationValues[groupBy]
								if (existingCalculationValue) {
									calculationValues[groupBy] = Math.max(existingCalculationValue, value)
								} else {
									calculationValues[groupBy] = value
								}
							} else {
								const existingCalculationValue = calculations[index]
								if (existingCalculationValue) {
									calculations[index] = Math.max(existingCalculationValue, value)
								} else {
									calculations[index] = value
								}
							}
						}
					}
					break
				case CalculationType.Min:
					calculationAppend = (index, calculations, caseValue, groupBy, insightGroupKey) => {
						var value = getNumericValue(caseValue, insightGroupKey)
						if (value != null) {
							if (groupBy) {
								const calculationValues = calculations[index]
								const existingCalculationValue = calculationValues[groupBy]
								if (existingCalculationValue) {
									calculationValues[groupBy] = Math.min(existingCalculationValue, value)
								} else {
									calculationValues[groupBy] = value
								}
							} else {
								const existingCalculationValue = calculations[index]
								if (existingCalculationValue) {
									calculations[index] = Math.min(existingCalculationValue, value)
								} else {
									calculations[index] = value
								}
							}
						}
					}
					break
			}

			calculationFunctionsList.push({
				key: calculation.key,
				calculationType: calculation.calcType,
				addValue: calculationAppend,
				groupBy: getGroupByValue
			})
		}

		const calculationResultsList: CalculationResults[] = []

		for (let [key, group] of Object.entries(groupedSearchResults.results)) {
			const groupResults: CalculationResults = {
				label: groupedSearchResults.labels[key] ? groupedSearchResults.labels[key] : key,
				value: key,
				calculations: {},
				calculationCounts: {},
				calculationTotals: [],
				results: group
			}

			for (let calculation of calculationFunctionsList) {
				if (calculation.groupBy) {
					groupResults.calculations[calculation.key] = {}
					groupResults.calculationCounts[calculation.key] = {}
				} else {
					groupResults.calculationCounts[calculation.key] = 0
				}
			}

			for (let caseResult of group) {
				for (let calculationFunction of calculationFunctionsList) {
					if (!calculationLabels[calculationFunction.key]) {
						calculationLabels[calculationFunction.key] = {}
					}
					const groupByLabels = calculationLabels[calculationFunction.key]

					if (calculationFunction.groupBy) {
						const groupBy = calculationFunction.groupBy(caseResult)
						const calcCounts = groupResults.calculationCounts[calculationFunction.key]
						if (this.isTupleArray(groupBy)) {
							for (let tuple of groupBy) {
								groupByLabels[tuple[1]] = tuple[0]
								calculationFunction.addValue(calculationFunction.key, groupResults.calculations, caseResult, tuple[1], key)

								const existingCount : number = calcCounts[tuple[1]]
								if (existingCount) {
									calcCounts[tuple[1]] = existingCount + 1
								} else {
									calcCounts[tuple[1]] = 1
								}
							}
						} else {
							const tuple = groupBy as [string, string]
							groupByLabels[tuple[1]] = tuple[0]

							calculationFunction.addValue(calculationFunction.key, groupResults.calculations, caseResult, tuple[1], key)

							const existingCount : number = calcCounts[tuple[1]]
							if (existingCount) {
								calcCounts[tuple[1]] = existingCount + 1
							} else {
								calcCounts[tuple[1]] = 1
							}
						}
					} else {
						calculationFunction.addValue(calculationFunction.key, groupResults.calculations, caseResult, null, key)

						const existingCount = groupResults.calculationCounts[calculationFunction.key]
						if (existingCount) {
							groupResults.calculationCounts[calculationFunction.key] = existingCount + 1
						} else {
							groupResults.calculationCounts[calculationFunction.key] = 1
						}
					}
				}
			}

			for (let calculationFunction of calculationFunctionsList
				.filter(c => c.calculationType === CalculationType.Average || c.calculationType === CalculationType.CountPerDay)) {
				if (calculationFunction.groupBy) {
					const totals = groupResults.calculations[calculationFunction.key]
					const counts = groupResults.calculationCounts[calculationFunction.key]

					if (totals && counts) {
						const calculationTotals = totals as { [key: string]: number }
						const calculationCounts = counts as { [key: string]: number }

						for (let [countKey, countValue] of Object.entries(calculationCounts)) {
							if (countValue === 0) {
								continue
							}

							const total = calculationTotals[countKey]

							if (total) {
								switch (calculationFunction.calculationType) {
									case CalculationType.Average:
										calculationTotals[countKey] = +(total / countValue).toFixed(2)
										break
									case CalculationType.CountPerDay:
										calculationTotals[countKey] = +(total / dayCount).toFixed(2)
										break
								}
							}
						}
					}
				} else {
					const calculationTotal = groupResults.calculations[calculationFunction.key] ? groupResults.calculations[calculationFunction.key] : 0
					const calculationCount = groupResults.calculationCounts[calculationFunction.key] ? groupResults.calculationCounts[calculationFunction.key] : 0

					if (calculationCount === 0 || calculationTotal === 0) {
						groupResults.calculations[calculationFunction.key] = 0
						continue
					}

					switch (calculationFunction.calculationType) {
						case (CalculationType.Average):
							groupResults.calculations[calculationFunction.key] = +(calculationTotal / calculationCount).toFixed(2)
							break
						case (CalculationType.CountPerDay):
							groupResults.calculations[calculationFunction.key] = +(calculationTotal / dayCount).toFixed(2)
							break
					}
				}
			}
			calculationResultsList.push(groupResults)
		}

		return {
			id: "",
			labelFormat: groupedSearchResults.labelFormat,
			calculationGroupByLabels: calculationLabels,
			results: _.sortBy(calculationResultsList, c => c.value),
			calculations: insight.calculations
		}
	}

	public applyCalculationTotals (results: CalculatedResults, insight: Insight, includedResults: IncludedInsight[]) {
		if (includedResults && includedResults.length > 0) {
			for (let includedInsight of includedResults) {
				for (let [key, groupByLabel] of Object.entries(includedInsight.results.calculationGroupByLabels)) {
					results.calculationGroupByLabels[key] = groupByLabel
				}

				for (let includedGroup of includedInsight.results.results) {
					const baseGroup = results.results.find(r => r.value === includedGroup.value)

					if (!baseGroup) {
						results.results.push({
							label: includedGroup.label,
							value: includedGroup.value,
							calculations: includedGroup.calculations,
							calculationCounts: {},
							calculationTotals: [],
							results: [],
							totalRecords: 0
						})
					} else {
						for (let [key, calculation] of Object.entries(includedGroup.calculations)) {
							baseGroup.calculations[key] = calculation.value
						}
					}
				}
			}
		}

		if (insight.calculationTotals) {
			for (let groupResults of results.results) {
				// Loop through each calculation total and sum up all the calculation results
				const calculationTotalResults = []
				if (insight.calculationTotals) {
					for (let calculationTotal of insight.calculationTotals) {
						// No grouping, results for each calculation are doubles, just sum directly
						if (!calculationTotal.groupBy) {
							let total = 0

							for (let calculationKey of calculationTotal.calculationList) {
								const existingCalculation = groupResults.calculations[calculationKey]
								if (existingCalculation && typeof existingCalculation === "number") {
									total += existingCalculation
								}
							}

							if (calculationTotal.subtractCalculationList) {
								for (let calculationKey of calculationTotal.subtractCalculationList) {
									const existingCalulation = groupResults.calculations[calculationKey]
									if (existingCalulation && typeof existingCalulation === "number") {
										total -= existingCalulation
									}
								}
							}

							calculationTotalResults.push(total)
						} else { // Grouping, calculation results are a dictionary so we need to sum by key in dictionary
							const total: { [key: string]: number } = {}
							let totalValue = 0

							// Loop through each calculation in the calculation total to sum by key
							for (let calculationKey in calculationTotal.calculationList) {
								// Calculations are in a dictionary with keys being their index position in insights.calculation
								const calculation = groupResults.calculations[calculationKey]

								if (typeof calculation === "object" && Object.keys(calculation).length) {
									const calcDictionary = calculation as { [key: string]: number }
									for (let [calcKey, result] of Object.entries(calcDictionary)) {
										if (calculationTotal.collate) {
											if (calculationTotal.collateInclude && !calculationTotal.collateInclude.find(include => include.id === calcKey)) {
												continue
											}
											if (calculationTotal.collateExclude && calculationTotal.collateExclude.find(exclude => exclude.id === calcKey)) {
												continue
											}

											totalValue += result
											continue
										}

										if (!total[calcKey]) {
											total[calcKey] = result
										} else {
											total[calcKey] += result
										}
									}
								}
							}

							if (calculationTotal.collate) {
								calculationTotalResults.push(totalValue)
							} else {
								calculationTotalResults.push(total)
							}
						}
					}
				}
				groupResults.calculationTotals = calculationTotalResults
			}
		}
	}

	private getLabeledValueFromProperty (caseDetails: CaseDetails, propertyName: string) : any {
		const value = caseDetails[propertyName]

		const caseColumn = caseColumns.find(c => c.key === propertyName)!

		if (caseColumn.fieldDataType &&
            (caseColumn.fieldDataType === DataType.arrayMultiple ||
                caseColumn.fieldDataType === DataType.arrayData)) {
			if (!value || value.length === 0) {
				return ["None", "None"]
			}

			const returnList = []
			for (let valueItem of value) {
				returnList.push([valueItem.label, valueItem.id])
			}

			return returnList
		} else if (propertyName === "assigneeId") {
			const label = this.getAssigneeLabel(caseDetails, value as string)
			return [label, value || "None"]
		} else {
			let label = value || "Not set"
			if (caseColumn.displayKey) {
				const displayPropertyName = caseColumn.displayKey
				label = caseDetails[displayPropertyName] || "Not set"
			}

			return [label, (value && value.toString()) || "Not set"]
		}
	}

	private getLabeledValueFromField (caseDetails: CaseDetails, fieldId: string, nullLabel: string | null) : any {
		if (!caseDetails.fields || !caseDetails.fields[fieldId]) {
			return ["Not set", "Not set"]
		}

		const caseField = caseDetails.fields[fieldId]

		if (caseField.dataType === "checkbox") {
			const checkboxValue = caseField.value ? "Yes" : "No"
			return [checkboxValue, checkboxValue]
		}

		const value = caseField.value

		if (dataTypeByFieldType[caseField.fieldDataType!] === DataType.arrayMultiple ||
            dataTypeByFieldType[caseField.fieldDataType!] === DataType.arrayData) {
			if (!value || value.length === 0) {
				return ["None", "None"]
			}

			const returnList = []
			for (let valueItem of value) {
				returnList.push([valueItem.label, valueItem.id])
			}

			return returnList
		} else if (this.isUserRole(value) ||
            dataTypeByFieldType[caseField.fieldDataType!] === DataType.userRole) {
			const returnList = []
			if (value && caseDetails.users) {
				const userRole = value as UserRole
				const caseUsers = caseDetails.users

				if (userRole.users) {
					const userRoleUsers = userRole.users
						.map(u => caseUsers.find(c => c.id === u.id))
						.filter(u => u)

					const labelTuples = userRoleUsers
						.map(u => [u!.name, u!.isExternal ? u!.name : u!.id])

					returnList.push(...labelTuples)
				}

				if (userRole.subRoles) {
					const subRoleUsers = userRole.subRoles
						.filter(subRole => subRole.users)
						.flatMap(subRole => subRole.users!.map(u => caseUsers.find(c => c.id === u.id)).filter(u => u))

					const labelTuples = subRoleUsers.map(u => [u!.name, u!.isExternal ? u!.name : u!.id])

					returnList.push(...labelTuples)
				}
			}

			if (returnList.length === 0) {
				return ["None", "None"]
			}

			return returnList
		} else {
			const labelToReturn = caseField.label
				? caseField.label
				: (value && value.toString()) || nullLabel

			const valueToReturn = (value && value.toString()) || nullLabel
			return [labelToReturn, valueToReturn]
		}
	}

	private getUsersTotalTime (users: CaseUser[] | null, userId: string | null) : number {
		if (!users) {
			return 0
		}
		if (userId === "External") {
			return users.filter(u => u.isExternal).map(u => u.timeSpent || 0).reduce((a, b) => a + b)
		}

		if (!userId) {
			return 0
		}
		const caseUser = users.find(u => u.id === userId)
		return (caseUser && caseUser.timeSpent) || 0
	}

	private getAssigneeLabel (caseDetails: CaseDetails, assigneeId: string) : string {
		if (!assigneeId) {
			return "None"
		}

		if (!caseDetails.users) {
			return assigneeId
		}

		const caseUser = caseDetails.users.firstOrDefault(u => u.id === assigneeId)

		if (!caseUser || !caseUser.name) {
			return assigneeId
		}

		return caseUser.name
	}

	private isTupleArray (array: any[]) : array is [any, any][] {
		if (array.length === 0) {
			return false
		}

		const firstElement = array[0]

		if (Array.isArray(firstElement)) {
			return true
		}
		return false
	}

	// DataService location: SVCases.Utils.Search.Clause.ValueDateTime
	private getClauseDate (clause: Clause, getEarliest: boolean) : DateTime | null {
		if (this.isDateRange(clause.valueObject)) {
			return getEarliest ? clause.valueObject.start : clause.valueObject.end
		}

		if (clause.valueObject instanceof DateTime) {
			return clause.valueObject
		}

		if (!clause.value) {
			return null
		}

		const parsedValue = DateTime.fromISO(clause.value, { zone: "utc" })

		if (parsedValue.isValid) {
			return parsedValue
		}

		return null
	}

	private getValueFromField (caseDetails: CaseDetails, fieldId: string) : any {
		return caseDetails.fields != null && caseDetails.fields[fieldId] ? caseDetails.fields[fieldId].value : null
	}

	private getValueFromBuiltInField (caseDetails: CaseDetails, fieldName: string) : any {
		return caseDetails[fieldName]
	}

	private isDateRange (value: any): value is DateRange {
		return value &&
            (value as DateRange).start !== undefined &&
            (value as DateRange).end !== undefined
	}

	private isUserRole (value: any): value is UserRole {
		const userRoleValue = value as UserRole
		return !!(userRoleValue && (userRoleValue.users || userRoleValue.subRoles))
	}

	private isTimeSpent (value: any) : value is TimeSpentAsValue {
		return value && (value as TimeSpentAsValue).calculationType === CalculatedFieldType.TimeSpentAs
	}

	private getDayCount (insight: Insight) : number {
		let dayCount = 1.0

		if (insight.calculations.some(c => c.calcType === CalculationType.CountPerDay)) {
			const allStartDates = insight.clauses.filter(clause =>
				clause.fieldDataType === DataType.dateTime &&
                clause.operator === Operator.GreaterThan)
				.map(clause => this.getClauseDate(clause, false))
				.filter(date => date !== null)

			if (allStartDates.length > 0) {
				const latestStartDate = _.max(allStartDates)!

				const endDates = insight.clauses.filter(clause =>
					clause.fieldDataType === DataType.dateTime &&
                    clause.operator === Operator.LessThan)
					.map(clause => this.getClauseDate(clause, true))
					.filter(date => date != null)

				const earliestEndDate = endDates.length > 0 ? _.min(endDates)! : DateTime.utc()

				const daysBetween = (earliestEndDate)
					.diff(latestStartDate,
						["days"])

				dayCount = Math.ceil(daysBetween.days)
			}
		}
		return dayCount
	}
}
